<script setup lang="ts">
import { ref, watch } from 'vue';
import { PlusIcon, XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import type { MenuItemVariant } from '@/types/menu';

interface Props {
  variants: MenuItemVariant[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:variants': [variants: MenuItemVariant[]];
  'add-variant': [];
  'edit-variant': [index: number];
  'remove-variant': [index: number];
}>();

const { t } = useI18n();
const variantMenus = ref<Record<string, boolean>>({});


const handleAddVariant = () => {
  emit('add-variant');
};

const editVariant = (index: number) => {
  emit('edit-variant', index);
};

const handleRemoveVariant = (index: number) => {
  emit('remove-variant', index);
};
</script>

<template>
  <div class="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-100">
        {{ t('app.forms.variants') }}
      </h3>
      <button
        type="button"
        class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        @click="handleAddVariant"
      >
        <PlusIcon class="-ml-0.5 mr-2 h-4 w-4" />
        {{ t('app.forms.add_variant') }}
      </button>
    </div>

    <!-- Variants List -->
    <div
      v-if="variants && variants.length > 0"
      class="mt-4 space-y-2"
    >
      <div 
        v-for="(variant, index) in variants" 
        :key="variant.id || `variant-${index}`"
        class="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 p-3"
      >
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-white">
            {{ variant.name }}
          </p>
          <div class="flex items-center gap-2">
            <p 
              v-if="variant.discount_price && variant.discount_price > 0"
              class="text-sm text-gray-500 dark:text-gray-400 line-through"
            >
              ${{ typeof variant.price === 'number' ? variant.price.toFixed(2) : variant.price }}
            </p>
            <p 
              class="text-sm font-medium"
              :class="variant.discount_price && variant.discount_price > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'"
            >
              ${{ variant.discount_price && variant.discount_price > 0 
                ? (typeof variant.discount_price === 'number' ? variant.discount_price.toFixed(2) : variant.discount_price)
                : (typeof variant.price === 'number' ? variant.price.toFixed(2) : variant.price) 
              }}
            </p>
            <span 
              v-if="variant.discount_price && variant.discount_price > 0"
              class="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-200"
            >
              {{ t('app.forms.sale_badge') }}
            </span>
          </div>
          <span 
            v-if="!variant.is_available"
            class="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-200"
          >
            {{ t('app.status.unavailable') }}
          </span>
        </div>
        <div class="flex-shrink-0">
          <DropdownMenu
            v-model="variantMenus[`variant-${index}`]"
            width="sm"
          >
            <DropdownMenuItem 
              :icon="PencilIcon" 
              variant="primary"
              @click="editVariant(index)"
            >
              {{ t('app.actions.edit') }}
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              :icon="TrashIcon" 
              variant="danger"
              @click="handleRemoveVariant(index)"
            >
              {{ t('app.actions.delete') }}
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>
    </div>
    <p
      v-else
      class="mt-2 text-sm text-gray-500 dark:text-gray-400"
    >
      {{ t('app.forms.no_variants') }}
    </p>
  </div>
</template>
